import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  Stack,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Collapse,
  IconButton,
  Link,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { get, post } from '../../services/apiService';

const LAUNCH_COLUMNS = [
  { id: 'launchName', label: 'Launch Name', align: 'left', numeric: false },
  { id: 'dateTime', label: 'Date time', align: 'right', numeric: false },
  { id: 'instance', label: 'Instance', align: 'left', numeric: false },
  { id: 'total', label: 'Total Tests', align: 'right', numeric: true },
  { id: 'passed', label: 'Passed', align: 'right', numeric: true },
  { id: 'failed', label: 'Failed', align: 'right', numeric: true },
  { id: 'skipped', label: 'Skipped', align: 'right', numeric: true },
  { id: 'passPercentage', label: 'Pass %', align: 'right', numeric: true }
];

/**
 * Release Execution Tab component
 * Provides Report Portal project selection and attribute filters (key-value pairs) for fetching test launches and results.
 */
const ReleaseExecutionTab = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState(null);
  const [savedFilters, setSavedFilters] = useState([]);
  const [selectedFilterId, setSelectedFilterId] = useState('');
  const [savedFiltersLoading, setSavedFiltersLoading] = useState(false);
  const [savedFiltersError, setSavedFiltersError] = useState(null);
  const [filterLaunches, setFilterLaunches] = useState([]);
  const [filterLaunchesLoading, setFilterLaunchesLoading] = useState(false);
  const [filterLaunchesError, setFilterLaunchesError] = useState(null);
  const [launchesSectionOpen, setLaunchesSectionOpen] = useState(true);
  const [orderBy, setOrderBy] = useState('dateTime');
  const [order, setOrder] = useState('desc');
  const [consolidateLoading, setConsolidateLoading] = useState(false);
  const [consolidateMessage, setConsolidateMessage] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [summaryModuleOpen, setSummaryModuleOpen] = useState(true);
  const [summarySuiteOpen, setSummarySuiteOpen] = useState(true);
  const [summaryInstanceOpen, setSummaryInstanceOpen] = useState(true);
  const [summaryConfigurationOpen, setSummaryConfigurationOpen] = useState(true);
  const [selectedModuleForGrid, setSelectedModuleForGrid] = useState('');
  const [testsByModule, setTestsByModule] = useState(null);
  const [testsByModuleLoading, setTestsByModuleLoading] = useState(false);
  const [testsByModuleError, setTestsByModuleError] = useState(null);
  const [testsByModuleSectionOpen, setTestsByModuleSectionOpen] = useState(true);
  const [summaryModuleOrderBy, setSummaryModuleOrderBy] = useState('name');
  const [summaryModuleOrder, setSummaryModuleOrder] = useState('asc');
  const [summarySuiteOrderBy, setSummarySuiteOrderBy] = useState('name');
  const [summarySuiteOrder, setSummarySuiteOrder] = useState('asc');
  const [summaryInstanceOrderBy, setSummaryInstanceOrderBy] = useState('name');
  const [summaryInstanceOrder, setSummaryInstanceOrder] = useState('asc');
  const [summaryConfigurationOrderBy, setSummaryConfigurationOrderBy] = useState('name');
  const [summaryConfigurationOrder, setSummaryConfigurationOrder] = useState('asc');
  const [gridOrderBy, setGridOrderBy] = useState('testid');
  const [gridOrder, setGridOrder] = useState('asc');
  const [analysisSectionOpen, setAnalysisSectionOpen] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);

  const fetchSummary = React.useCallback(async () => {
    try {
      setSummaryLoading(true);
      setSummaryError(null);
      const res = await get('/api/report-portal/analysis-summary');
      if (res?.success && res?.data) {
        setSummaryData(res.data);
      } else {
        setSummaryError(res?.message || 'Failed to load summary');
        setSummaryData(null);
      }
    } catch (err) {
      setSummaryError(err.message || 'Failed to load summary');
      setSummaryData(null);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const fetchAnalysis = React.useCallback(async () => {
    try {
      setAnalysisLoading(true);
      setAnalysisError(null);
      const res = await get('/api/report-portal/analysis-ado-match');
      if (res?.success && res?.data) {
        setAnalysisData(res.data);
      } else {
        setAnalysisError(res?.message || 'Failed to load analysis');
        setAnalysisData(null);
      }
    } catch (err) {
      setAnalysisError(err.message || 'Failed to load analysis');
      setAnalysisData(null);
    } finally {
      setAnalysisLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchProjects = async () => {
      try {
        setProjectsLoading(true);
        setProjectsError(null);
        const res = await get('/api/report-portal/projects');
        if (!cancelled && res?.data) {
          const list = Array.isArray(res.data) ? res.data : res.data?.content || [];
          setProjects(list);
          if (list.length > 0 && !selectedProject) {
            setSelectedProject(list[0]);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setProjectsError(err.message || 'Failed to load Report Portal projects');
          setProjects([]);
        }
      } finally {
        if (!cancelled) setProjectsLoading(false);
      }
    };
    fetchProjects();
    return () => { cancelled = true; };
  }, []);

  const handleProjectChange = (event) => {
    const next = event.target.value;
    setSelectedProject(next);
    setSelectedFilterId('');
    setSavedFilters([]);
    setFilterLaunches([]);
    setFilterLaunchesError(null);
    if (!next) {
      setSavedFiltersError(null);
      return;
    }
  };

  // When project changes, refresh saved filters list for the new section
  useEffect(() => {
    if (!selectedProject) {
      setSavedFilters([]);
      setSavedFiltersError(null);
      return;
    }
    let cancelled = false;
    const fetchFilters = async () => {
      try {
        setSavedFiltersLoading(true);
        setSavedFiltersError(null);
        const res = await get('/api/report-portal/filters', { projectName: selectedProject });
        if (!cancelled && res?.data) {
          const list = Array.isArray(res.data) ? res.data : [];
          setSavedFilters(list);
          setSelectedFilterId('');
        }
      } catch (err) {
        if (!cancelled) {
          setSavedFiltersError(err.message || 'Failed to load filters');
          setSavedFilters([]);
        }
      } finally {
        if (!cancelled) setSavedFiltersLoading(false);
      }
    };
    fetchFilters();
    return () => { cancelled = true; };
  }, [selectedProject]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    if (!selectedModuleForGrid) {
      setTestsByModule(null);
      setTestsByModuleError(null);
      return;
    }
    let cancelled = false;
    const fetchTestsByModule = async () => {
      try {
        setTestsByModuleLoading(true);
        setTestsByModuleError(null);
        const moduleParam = selectedModuleForGrid === '(empty)' ? '' : selectedModuleForGrid;
        const res = await get('/api/report-portal/analysis-by-module', { module: moduleParam });
        if (cancelled) return;
        if (res?.success && res?.data) {
          setTestsByModule(res.data);
        } else {
          setTestsByModuleError(res?.message || 'Failed to load tests');
          setTestsByModule(null);
        }
      } catch (err) {
        if (!cancelled) {
          setTestsByModuleError(err.message || 'Failed to load tests');
          setTestsByModule(null);
        }
      } finally {
        if (!cancelled) setTestsByModuleLoading(false);
      }
    };
    fetchTestsByModule();
    return () => { cancelled = true; };
  }, [selectedModuleForGrid]);

  const handleFilterChange = (event) => {
    setSelectedFilterId(event.target.value);
    setFilterLaunchesError(null);
  };

  const handleGetRunsByFilter = async () => {
    if (!selectedFilterId || !selectedProject) return;
    setFilterLaunchesError(null);
    try {
      setFilterLaunchesLoading(true);
      setFilterLaunches([]);
      const res = await post('/api/report-portal/launches-by-filter', {
        projectName: selectedProject,
        filterId: selectedFilterId
      });
      if (res && res.success && Array.isArray(res.data)) {
        setFilterLaunches(res.data);
      } else {
        setFilterLaunchesError(res?.message || 'No launch data returned.');
      }
    } catch (err) {
      setFilterLaunchesError(err.message || 'Failed to fetch runs.');
      setFilterLaunches([]);
    } finally {
      setFilterLaunchesLoading(false);
    }
  };

  const handleConsolidateRuns = async (e) => {
    e.stopPropagation();
    if (!selectedFilterId || !selectedProject) return;
    setConsolidateMessage(null);
    try {
      setConsolidateLoading(true);
      const res = await post('/api/report-portal/consolidate-runs', {
        projectName: selectedProject,
        filterId: selectedFilterId
      });
      if (res && res.success) {
        setConsolidateMessage({
          severity: 'success',
          text: res.message || `Consolidated ${res.uniqueTests ?? 0} unique tests from ${res.launchesProcessed ?? 0} launches.`
        });
        fetchSummary();
      } else {
        setConsolidateMessage({ severity: 'warning', text: res?.message || 'Consolidation failed.' });
      }
    } catch (err) {
      setConsolidateMessage({ severity: 'error', text: err.message || 'Failed to consolidate runs.' });
    } finally {
      setConsolidateLoading(false);
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortLaunches = (arr, orderByKey, orderDir) => {
    const a = [...arr];
    a.sort((x, y) => {
      const aVal = x[orderByKey];
      const bVal = y[orderByKey];
      if (orderByKey === 'dateTime' || orderByKey === 'launchName' || orderByKey === 'instance') {
        const cmp = String(aVal ?? '').localeCompare(String(bVal ?? ''), undefined, { numeric: true });
        return orderDir === 'asc' ? cmp : -cmp;
      }
      const aNum = Number(aVal);
      const bNum = Number(bVal);
      return orderDir === 'asc' ? aNum - bNum : bNum - aNum;
    });
    return a;
  };

  const sortedFilterLaunches = useMemo(
    () => sortLaunches(filterLaunches, orderBy, order),
    [filterLaunches, orderBy, order]
  );

  const summaryRowValue = (row, key) => {
    const total = (row.pass || 0) + (row.fail || 0) + (row.skip || 0) + (row.notRun || 0);
    if (key === 'total') return total;
    if (key === 'passPct') return total > 0 ? Math.round(((row.pass || 0) / total) * 100) : 0;
    if (key === 'name') return row.name === '' ? '(empty)' : row.name;
    return row[key] ?? 0;
  };

  const sortSummaryRows = (rows, orderByKey, orderDir) => {
    const a = [...rows];
    a.sort((x, y) => {
      const xVal = summaryRowValue(x, orderByKey);
      const yVal = summaryRowValue(y, orderByKey);
      const cmp = typeof xVal === 'string'
        ? String(xVal).localeCompare(String(yVal), undefined, { numeric: true })
        : Number(xVal) - Number(yVal);
      return orderDir === 'asc' ? cmp : -cmp;
    });
    return a;
  };

  const createSummarySortHandler = (setOrderBy, setOrder, orderBy, order) => (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedModuleRows = useMemo(
    () => sortSummaryRows(summaryData?.byModule || [], summaryModuleOrderBy, summaryModuleOrder),
    [summaryData?.byModule, summaryModuleOrderBy, summaryModuleOrder]
  );
  const sortedSuiteRows = useMemo(
    () => sortSummaryRows(summaryData?.bySuite || [], summarySuiteOrderBy, summarySuiteOrder),
    [summaryData?.bySuite, summarySuiteOrderBy, summarySuiteOrder]
  );
  const sortedInstanceRows = useMemo(
    () => sortSummaryRows(summaryData?.byInstance || [], summaryInstanceOrderBy, summaryInstanceOrder),
    [summaryData?.byInstance, summaryInstanceOrderBy, summaryInstanceOrder]
  );
  const sortedConfigurationRows = useMemo(
    () => sortSummaryRows(summaryData?.byConfiguration || [], summaryConfigurationOrderBy, summaryConfigurationOrder),
    [summaryData?.byConfiguration, summaryConfigurationOrderBy, summaryConfigurationOrder]
  );

  const sortGridTests = (tests, orderByKey, orderDir) => {
    const a = [...(tests || [])];
    a.sort((x, y) => {
      let xVal = x[orderByKey];
      let yVal = y[orderByKey];
      if (orderByKey === 'runs') {
        xVal = (x.runs || []).length;
        yVal = (y.runs || []).length;
      } else if (orderByKey === 'file') {
        xVal = x.file || x.config || '';
        yVal = y.file || y.config || '';
      }
      const cmp = typeof xVal === 'string' || typeof yVal === 'string'
        ? String(xVal ?? '').localeCompare(String(yVal ?? ''), undefined, { numeric: true })
        : Number(xVal ?? 0) - Number(yVal ?? 0);
      return orderDir === 'asc' ? cmp : -cmp;
    });
    return a;
  };

  const handleGridSort = (e, property) => {
    const isAsc = gridOrderBy === property && gridOrder === 'asc';
    setGridOrder(isAsc ? 'desc' : 'asc');
    setGridOrderBy(property);
  };

  const sortedGridTests = useMemo(
    () => testsByModule?.tests ? sortGridTests(testsByModule.tests, gridOrderBy, gridOrder) : [],
    [testsByModule?.tests, gridOrderBy, gridOrder]
  );

  const SUMMARY_COLUMNS = [
    { id: 'name', label: 'Module', align: 'left' },
    { id: 'pass', label: 'Pass', align: 'right' },
    { id: 'fail', label: 'Fail', align: 'right' },
    { id: 'skip', label: 'Skip', align: 'right' },
    { id: 'notRun', label: 'Not run', align: 'right' },
    { id: 'total', label: 'Total', align: 'right' },
    { id: 'passPct', label: 'Pass %', align: 'right' }
  ];

  const SUITE_COLUMNS = [
    { id: 'name', label: 'Suite', align: 'left' },
    { id: 'pass', label: 'Pass', align: 'right' },
    { id: 'fail', label: 'Fail', align: 'right' },
    { id: 'skip', label: 'Skip', align: 'right' },
    { id: 'notRun', label: 'Not run', align: 'right' },
    { id: 'total', label: 'Total', align: 'right' },
    { id: 'passPct', label: 'Pass %', align: 'right' }
  ];

  const INSTANCE_COLUMNS = [
    { id: 'name', label: 'Instance', align: 'left' },
    { id: 'pass', label: 'Pass', align: 'right' },
    { id: 'fail', label: 'Fail', align: 'right' },
    { id: 'skip', label: 'Skip', align: 'right' },
    { id: 'notRun', label: 'Not run', align: 'right' },
    { id: 'total', label: 'Total', align: 'right' },
    { id: 'passPct', label: 'Pass %', align: 'right' }
  ];

  const CONFIGURATION_COLUMNS = [
    { id: 'name', label: 'Configuration', align: 'left' },
    { id: 'pass', label: 'Pass', align: 'right' },
    { id: 'fail', label: 'Fail', align: 'right' },
    { id: 'skip', label: 'Skip', align: 'right' },
    { id: 'notRun', label: 'Not run', align: 'right' },
    { id: 'total', label: 'Total', align: 'right' },
    { id: 'passPct', label: 'Pass %', align: 'right' }
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Release Execution
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Select a Report Portal project and saved filter, then use &quot;Get Runs&quot; to load launches. Consolidate runs to build the summary and tests grid.
      </Typography>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Report Portal
        </Typography>
        {projectsError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {projectsError}
          </Alert>
        )}
        {savedFiltersError && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setSavedFiltersError(null)}>
            {savedFiltersError}
          </Alert>
        )}
        {projectsLoading ? (
          <Stack direction="row" alignItems="center" spacing={2}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Loading projects from Report Portal…
            </Typography>
          </Stack>
        ) : (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-end" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 380 }}>
              <InputLabel id="report-portal-project-label">Project</InputLabel>
              <Select
                labelId="report-portal-project-label"
                id="report-portal-project"
                value={selectedProject}
                label="Project"
                onChange={handleProjectChange}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {projects.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 380 }} disabled={!selectedProject}>
              <InputLabel id="saved-filter-label">Filter</InputLabel>
              <Select
                labelId="saved-filter-label"
                id="saved-filter"
                value={selectedFilterId}
                label="Filter"
                onChange={handleFilterChange}
                displayEmpty
                sx={{
                  '& .MuiSelect-select': {
                    paddingRight: '40px',
                    minHeight: '1.4375em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              >
                <MenuItem value="">
                  <em>{selectedProject ? (savedFiltersLoading ? 'Loading…' : 'Select a filter') : 'Select a project first'}</em>
                </MenuItem>
                {savedFilters.map((f) => (
                  <MenuItem key={f.id} value={f.id}>
                    {f.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="primary"
              startIcon={filterLaunchesLoading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
              onClick={handleGetRunsByFilter}
              disabled={!selectedFilterId || filterLaunchesLoading}
              aria-label="Get runs"
            >
              Get Runs
            </Button>
          </Stack>
        )}
      </Paper>

      {filterLaunchesError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setFilterLaunchesError(null)}>
          {filterLaunchesError}
        </Alert>
      )}

      {consolidateMessage && (
        <Alert
          severity={consolidateMessage.severity}
          sx={{ mb: 2 }}
          onClose={() => setConsolidateMessage(null)}
        >
          {consolidateMessage.text}
        </Alert>
      )}

      {filterLaunches.length > 0 && (
        <Paper elevation={2} sx={{ overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              gap: 2,
              flexWrap: 'wrap'
            }}
          >
            <Box
              onClick={() => setLaunchesSectionOpen((o) => !o)}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flex: 1, minWidth: 0 }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Report Portal Launches ({filterLaunches.length})
              </Typography>
              <IconButton size="small" aria-label={launchesSectionOpen ? 'Collapse' : 'Expand'}>
                {launchesSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Button
              variant="outlined"
              size="small"
              disabled={!selectedFilterId || consolidateLoading}
              onClick={handleConsolidateRuns}
              startIcon={consolidateLoading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Consolidate runs
            </Button>
          </Box>
          <Collapse in={launchesSectionOpen}>
            <TableContainer sx={{ overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {LAUNCH_COLUMNS.map((col) => (
                      <TableCell
                        key={col.id}
                        variant="head"
                        align={col.align}
                        sortDirection={orderBy === col.id ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? order : 'asc'}
                          onClick={(e) => handleRequestSort(e, col.id)}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedFilterLaunches.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{row.launchName}</TableCell>
                      <TableCell align="right">{row.dateTime}</TableCell>
                      <TableCell>{row.instance || '—'}</TableCell>
                      <TableCell align="right">{row.total}</TableCell>
                      <TableCell align="right">{row.passed}</TableCell>
                      <TableCell align="right">{row.failed}</TableCell>
                      <TableCell align="right">{row.skipped}</TableCell>
                      <TableCell align="right">{row.passPercentage}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Paper>
      )}

      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
        Summary
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Pass / Fail (and Skip, Not run) breakdown from the consolidated analysis. Load or refresh after &quot;Consolidate runs&quot;.
      </Typography>
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            disabled={summaryLoading}
            onClick={fetchSummary}
            startIcon={summaryLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Refresh summary
          </Button>
          {summaryError && (
            <Typography variant="body2" color="error">
              {summaryError}
            </Typography>
          )}
        </Stack>
        {summaryLoading && !summaryData && (
          <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 4 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Loading summary…
            </Typography>
          </Stack>
        )}
        {summaryData && !summaryLoading && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <Box
                  onClick={() => setSummaryModuleOpen((o) => !o)}
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', p: 2 }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  By Module — Pass / Fail
                </Typography>
                <IconButton size="small" aria-label={summaryModuleOpen ? 'Collapse' : 'Expand'}>
                  {summaryModuleOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={summaryModuleOpen}>
                <TableContainer sx={{ overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {SUMMARY_COLUMNS.map((col) => (
                          <TableCell key={col.id} variant="head" align={col.align} sortDirection={summaryModuleOrderBy === col.id ? summaryModuleOrder : false}>
                            <TableSortLabel
                              active={summaryModuleOrderBy === col.id}
                              direction={summaryModuleOrderBy === col.id ? summaryModuleOrder : 'asc'}
                              onClick={(e) => createSummarySortHandler(setSummaryModuleOrderBy, setSummaryModuleOrder, summaryModuleOrderBy, summaryModuleOrder)(e, col.id)}
                            >
                              {col.label}
                            </TableSortLabel>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedModuleRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            No data. Run &quot;Consolidate runs&quot; first.
                          </TableCell>
                        </TableRow>
                      ) : (
                        <>
                          {sortedModuleRows.map((row, idx) => {
                            const total = (row.pass || 0) + (row.fail || 0) + (row.skip || 0) + (row.notRun || 0);
                            const passPct = total > 0 ? Math.round(((row.pass || 0) / total) * 100) : 0;
                            return (
                              <TableRow key={idx}>
                                <TableCell>{row.name === '' ? '(empty)' : row.name}</TableCell>
                                <TableCell align="right">{row.pass ?? 0}</TableCell>
                                <TableCell align="right">{row.fail ?? 0}</TableCell>
                                <TableCell align="right">{row.skip ?? 0}</TableCell>
                                <TableCell align="right">{row.notRun ?? 0}</TableCell>
                                <TableCell align="right">{total}</TableCell>
                                <TableCell align="right">{passPct}%</TableCell>
                              </TableRow>
                            );
                          })}
                          <TableRow sx={{ fontWeight: 600, backgroundColor: 'action.hover' }}>
                            <TableCell>Total</TableCell>
                            <TableCell align="right">
                              {(summaryData.byModule || []).reduce((s, r) => s + (r.pass || 0), 0)}
                            </TableCell>
                            <TableCell align="right">
                              {(summaryData.byModule || []).reduce((s, r) => s + (r.fail || 0), 0)}
                            </TableCell>
                            <TableCell align="right">
                              {(summaryData.byModule || []).reduce((s, r) => s + (r.skip || 0), 0)}
                            </TableCell>
                            <TableCell align="right">
                              {(summaryData.byModule || []).reduce((s, r) => s + (r.notRun || 0), 0)}
                            </TableCell>
                            <TableCell align="right">
                              {(summaryData.byModule || []).reduce((s, r) => s + (r.pass || 0) + (r.fail || 0) + (r.skip || 0) + (r.notRun || 0), 0)}
                            </TableCell>
                            <TableCell align="right">
                              {(() => {
                                const rows = summaryData.byModule || [];
                                const sumPass = rows.reduce((s, r) => s + (r.pass || 0), 0);
                                const grand = rows.reduce((s, r) => s + (r.pass || 0) + (r.fail || 0) + (r.skip || 0) + (r.notRun || 0), 0);
                                return grand > 0 ? `${Math.round((sumPass / grand) * 100)}%` : '0%';
                              })()}
                            </TableCell>
                          </TableRow>
                        </>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  <Box
                    onClick={() => setSummarySuiteOpen((o) => !o)}
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', p: 2 }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  By Suite — Pass / Fail
                </Typography>
                <IconButton size="small" aria-label={summarySuiteOpen ? 'Collapse' : 'Expand'}>
                  {summarySuiteOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={summarySuiteOpen}>
                <TableContainer sx={{ overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {SUITE_COLUMNS.map((col) => (
                          <TableCell key={col.id} variant="head" align={col.align} sortDirection={summarySuiteOrderBy === col.id ? summarySuiteOrder : false}>
                            <TableSortLabel
                              active={summarySuiteOrderBy === col.id}
                              direction={summarySuiteOrderBy === col.id ? summarySuiteOrder : 'asc'}
                              onClick={(e) => createSummarySortHandler(setSummarySuiteOrderBy, setSummarySuiteOrder, summarySuiteOrderBy, summarySuiteOrder)(e, col.id)}
                            >
                              {col.label}
                            </TableSortLabel>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedSuiteRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            No data. Run &quot;Consolidate runs&quot; first.
                          </TableCell>
                        </TableRow>
                      ) : (
                        <>
                          {sortedSuiteRows.map((row, idx) => {
                            const total = (row.pass || 0) + (row.fail || 0) + (row.skip || 0) + (row.notRun || 0);
                            const passPct = total > 0 ? Math.round(((row.pass || 0) / total) * 100) : 0;
                            return (
                              <TableRow key={idx}>
                                <TableCell>{row.name === '' ? '(empty)' : row.name}</TableCell>
                                <TableCell align="right">{row.pass ?? 0}</TableCell>
                                <TableCell align="right">{row.fail ?? 0}</TableCell>
                                <TableCell align="right">{row.skip ?? 0}</TableCell>
                                <TableCell align="right">{row.notRun ?? 0}</TableCell>
                                <TableCell align="right">{total}</TableCell>
                                <TableCell align="right">{passPct}%</TableCell>
                              </TableRow>
                            );
                          })}
                          <TableRow sx={{ fontWeight: 600, backgroundColor: 'action.hover' }}>
                            <TableCell>Total</TableCell>
                            <TableCell align="right">
                              {(summaryData.bySuite || []).reduce((s, r) => s + (r.pass || 0), 0)}
                            </TableCell>
                            <TableCell align="right">
                              {(summaryData.bySuite || []).reduce((s, r) => s + (r.fail || 0), 0)}
                            </TableCell>
                            <TableCell align="right">
                              {(summaryData.bySuite || []).reduce((s, r) => s + (r.skip || 0), 0)}
                            </TableCell>
                            <TableCell align="right">
                              {(summaryData.bySuite || []).reduce((s, r) => s + (r.notRun || 0), 0)}
                            </TableCell>
                            <TableCell align="right">
                              {(summaryData.bySuite || []).reduce((s, r) => s + (r.pass || 0) + (r.fail || 0) + (r.skip || 0) + (r.notRun || 0), 0)}
                            </TableCell>
                            <TableCell align="right">
                              {(() => {
                                const rows = summaryData.bySuite || [];
                                const sumPass = rows.reduce((s, r) => s + (r.pass || 0), 0);
                                const grand = rows.reduce((s, r) => s + (r.pass || 0) + (r.fail || 0) + (r.skip || 0) + (r.notRun || 0), 0);
                                return grand > 0 ? `${Math.round((sumPass / grand) * 100)}%` : '0%';
                              })()}
                            </TableCell>
                          </TableRow>
                        </>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
                </Paper>
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  <Box
                    onClick={() => setSummaryInstanceOpen((o) => !o)}
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', p: 2 }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  By Instance — Pass / Fail
                </Typography>
                <IconButton size="small" aria-label={summaryInstanceOpen ? 'Collapse' : 'Expand'}>
                  {summaryInstanceOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={summaryInstanceOpen}>
                <TableContainer sx={{ overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {INSTANCE_COLUMNS.map((col) => (
                          <TableCell key={col.id} variant="head" align={col.align} sortDirection={summaryInstanceOrderBy === col.id ? summaryInstanceOrder : false}>
                            <TableSortLabel
                              active={summaryInstanceOrderBy === col.id}
                              direction={summaryInstanceOrderBy === col.id ? summaryInstanceOrder : 'asc'}
                              onClick={(e) => createSummarySortHandler(setSummaryInstanceOrderBy, setSummaryInstanceOrder, summaryInstanceOrderBy, summaryInstanceOrder)(e, col.id)}
                            >
                              {col.label}
                            </TableSortLabel>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedInstanceRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            No data. Run &quot;Consolidate runs&quot; first.
                          </TableCell>
                        </TableRow>
                      ) : (
                        <>
                          {sortedInstanceRows.map((row, idx) => {
                            const total = (row.pass || 0) + (row.fail || 0) + (row.skip || 0) + (row.notRun || 0);
                            const passPct = total > 0 ? Math.round(((row.pass || 0) / total) * 100) : 0;
                            return (
                              <TableRow key={idx}>
                                <TableCell>{row.name === '' ? '(empty)' : row.name}</TableCell>
                                <TableCell align="right">{row.pass ?? 0}</TableCell>
                                <TableCell align="right">{row.fail ?? 0}</TableCell>
                                <TableCell align="right">{row.skip ?? 0}</TableCell>
                                <TableCell align="right">{row.notRun ?? 0}</TableCell>
                                <TableCell align="right">{total}</TableCell>
                                <TableCell align="right">{passPct}%</TableCell>
                              </TableRow>
                            );
                          })}
                          <TableRow sx={{ fontWeight: 600, backgroundColor: 'action.hover' }}>
                            <TableCell>Total</TableCell>
                            <TableCell align="right">
                              {(summaryData.byInstance || []).reduce((s, r) => s + (r.pass || 0), 0)}
                            </TableCell>
                            <TableCell align="right">
                              {(summaryData.byInstance || []).reduce((s, r) => s + (r.fail || 0), 0)}
                            </TableCell>
                            <TableCell align="right">
                              {(summaryData.byInstance || []).reduce((s, r) => s + (r.skip || 0), 0)}
                            </TableCell>
                            <TableCell align="right">
                              {(summaryData.byInstance || []).reduce((s, r) => s + (r.notRun || 0), 0)}
                            </TableCell>
                            <TableCell align="right">
                              {(summaryData.byInstance || []).reduce((s, r) => s + (r.pass || 0) + (r.fail || 0) + (r.skip || 0) + (r.notRun || 0), 0)}
                            </TableCell>
                            <TableCell align="right">
                              {(() => {
                                const rows = summaryData.byInstance || [];
                                const sumPass = rows.reduce((s, r) => s + (r.pass || 0), 0);
                                const grand = rows.reduce((s, r) => s + (r.pass || 0) + (r.fail || 0) + (r.skip || 0) + (r.notRun || 0), 0);
                                return grand > 0 ? `${Math.round((sumPass / grand) * 100)}%` : '0%';
                              })()}
                            </TableCell>
                          </TableRow>
                        </>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
                </Paper>
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  <Box
                    onClick={() => setSummaryConfigurationOpen((o) => !o)}
                    sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', p: 2 }}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      By Configuration — Pass / Fail
                    </Typography>
                    <IconButton size="small" aria-label={summaryConfigurationOpen ? 'Collapse' : 'Expand'}>
                      {summaryConfigurationOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                  <Collapse in={summaryConfigurationOpen}>
                    <TableContainer sx={{ overflow: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {CONFIGURATION_COLUMNS.map((col) => (
                              <TableCell key={col.id} variant="head" align={col.align} sortDirection={summaryConfigurationOrderBy === col.id ? summaryConfigurationOrder : false}>
                                <TableSortLabel
                                  active={summaryConfigurationOrderBy === col.id}
                                  direction={summaryConfigurationOrderBy === col.id ? summaryConfigurationOrder : 'asc'}
                                  onClick={(e) => createSummarySortHandler(setSummaryConfigurationOrderBy, setSummaryConfigurationOrder, summaryConfigurationOrderBy, summaryConfigurationOrder)(e, col.id)}
                                >
                                  {col.label}
                                </TableSortLabel>
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedConfigurationRows.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} align="center">
                                No data. Run &quot;Consolidate runs&quot; first.
                              </TableCell>
                            </TableRow>
                          ) : (
                            <>
                              {sortedConfigurationRows.map((row, idx) => {
                                const total = (row.pass || 0) + (row.fail || 0) + (row.skip || 0) + (row.notRun || 0);
                                const passPct = total > 0 ? Math.round(((row.pass || 0) / total) * 100) : 0;
                                return (
                                  <TableRow key={idx}>
                                    <TableCell>{row.name === '' ? '(empty)' : row.name}</TableCell>
                                    <TableCell align="right">{row.pass ?? 0}</TableCell>
                                    <TableCell align="right">{row.fail ?? 0}</TableCell>
                                    <TableCell align="right">{row.skip ?? 0}</TableCell>
                                    <TableCell align="right">{row.notRun ?? 0}</TableCell>
                                    <TableCell align="right">{total}</TableCell>
                                    <TableCell align="right">{passPct}%</TableCell>
                                  </TableRow>
                                );
                              })}
                              <TableRow sx={{ fontWeight: 600, backgroundColor: 'action.hover' }}>
                                <TableCell>Total</TableCell>
                                <TableCell align="right">
                                  {(summaryData.byConfiguration || []).reduce((s, r) => s + (r.pass || 0), 0)}
                                </TableCell>
                                <TableCell align="right">
                                  {(summaryData.byConfiguration || []).reduce((s, r) => s + (r.fail || 0), 0)}
                                </TableCell>
                                <TableCell align="right">
                                  {(summaryData.byConfiguration || []).reduce((s, r) => s + (r.skip || 0), 0)}
                                </TableCell>
                                <TableCell align="right">
                                  {(summaryData.byConfiguration || []).reduce((s, r) => s + (r.notRun || 0), 0)}
                                </TableCell>
                                <TableCell align="right">
                                  {(summaryData.byConfiguration || []).reduce((s, r) => s + (r.pass || 0) + (r.fail || 0) + (r.skip || 0) + (r.notRun || 0), 0)}
                                </TableCell>
                                <TableCell align="right">
                                  {(() => {
                                    const rows = summaryData.byConfiguration || [];
                                    const sumPass = rows.reduce((s, r) => s + (r.pass || 0), 0);
                                    const grand = rows.reduce((s, r) => s + (r.pass || 0) + (r.fail || 0) + (r.skip || 0) + (r.notRun || 0), 0);
                                    return grand > 0 ? `${Math.round((sumPass / grand) * 100)}%` : '0%';
                                  })()}
                                </TableCell>
                              </TableRow>
                            </>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Collapse>
                </Paper>
              </Stack>
            </Grid>
          </Grid>
        )}
      </Paper>

      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
        Analysis
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Compare ADO tests (Automated-UI, Automated-API) with tests in Report Portal. Tests in ADO but not in the current run are listed with links to ADO.
      </Typography>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box
          onClick={() => {
            setAnalysisSectionOpen((o) => !o);
            if (!analysisSectionOpen && !analysisData && !analysisLoading) fetchAnalysis();
          }}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', p: 2 }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            ADO vs Report Portal
          </Typography>
          <IconButton size="small" aria-label={analysisSectionOpen ? 'Collapse' : 'Expand'}>
            {analysisSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={analysisSectionOpen}>
          <Box sx={{ p: 2, pt: 0 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); fetchAnalysis(); }} disabled={analysisLoading}>
                {analysisLoading ? 'Loading…' : 'Refresh analysis'}
              </Button>
            </Stack>
            {analysisError && (
              <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setAnalysisError(null)}>
                {analysisError}
              </Alert>
            )}
            {analysisLoading && !analysisData && (
              <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Loading analysis…
                </Typography>
              </Stack>
            )}
            {analysisData && !analysisLoading && (
              <>
                <Typography variant="body2" paragraph>
                  <strong>ADO automated tests (Automated-UI, Automated-API):</strong> {analysisData.adoAutomatedTotal ?? 0}
                  {' · '}
                  <strong>Matched (in Report Portal):</strong> {analysisData.matchedCount ?? 0}
                  {' · '}
                  <strong>In ADO but not in Report Portal:</strong> {analysisData.notInRpCount ?? 0}
                </Typography>
                {analysisData.notInRpCount > 0 && (
                  <>
                    <Typography variant="body2" paragraph sx={{ mt: 0 }}>
                      <strong>By team:</strong>{' '}
                      {(analysisData.byTeam || []).map(({ name, count }) => `${name}: ${count}`).join(' · ')}
                    </Typography>
                    <Typography variant="body2" paragraph sx={{ mt: 0 }}>
                      <strong>By test suite:</strong>{' '}
                      {(analysisData.byTestSuite || []).map(({ name, count }) => `${name}: ${count}`).join(' · ')}
                    </Typography>
                    <Typography variant="body2" paragraph sx={{ mt: 0 }}>
                      <strong>By automation status:</strong>{' '}
                      {(analysisData.byAutomationStatus || []).map(({ name, count }) => `${name}: ${count}`).join(' · ')}
                    </Typography>
                  </>
                )}
                {analysisData.notInRp && analysisData.notInRp.length > 0 ? (
                  <TableContainer sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell variant="head" sx={{ minWidth: 80, whiteSpace: 'nowrap' }}>ID</TableCell>
                          <TableCell variant="head" sx={{ minWidth: 200, whiteSpace: 'nowrap' }}>Title</TableCell>
                          <TableCell variant="head" sx={{ minWidth: 120, whiteSpace: 'nowrap' }}>Team</TableCell>
                          <TableCell variant="head" sx={{ minWidth: 130, whiteSpace: 'nowrap' }}>Automation status</TableCell>
                          <TableCell variant="head" sx={{ minWidth: 100, whiteSpace: 'nowrap' }}>Test suite</TableCell>
                          <TableCell variant="head" sx={{ minWidth: 110, whiteSpace: 'nowrap' }}>Created date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analysisData.notInRp.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell sx={{ minWidth: 80 }}>
                              {row.adoUrl ? (
                                <Link href={row.adoUrl} target="_blank" rel="noopener noreferrer">
                                  {row.id}
                                </Link>
                              ) : (
                                row.id
                              )}
                            </TableCell>
                            <TableCell sx={{ minWidth: 200 }}>{row.title || '—'}</TableCell>
                            <TableCell sx={{ minWidth: 120 }}>{row.team || '—'}</TableCell>
                            <TableCell sx={{ minWidth: 130 }}>{row.automationStatus || '—'}</TableCell>
                            <TableCell sx={{ minWidth: 100 }}>{row.testSuite || '—'}</TableCell>
                            <TableCell sx={{ minWidth: 110 }}>
                              {row.createdDate
                                ? new Date(row.createdDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                                : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    All ADO automated tests in scope are present in Report Portal for this run.
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Collapse>
      </Paper>

      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
        Tests by module
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Select a module to see all tests in a grid: testCaseId, Module, Instance, File, Name, Result (recent status), and Runs (count).
      </Typography>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box
          onClick={() => setTestsByModuleSectionOpen((o) => !o)}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', p: 2 }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Tests grid
          </Typography>
          <IconButton size="small" aria-label={testsByModuleSectionOpen ? 'Collapse' : 'Expand'}>
            {testsByModuleSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={testsByModuleSectionOpen}>
          <Box sx={{ p: 2, pt: 0 }}>
            <FormControl size="small" sx={{ minWidth: 280, mb: 2 }} disabled={!summaryData}>
              <InputLabel id="tests-by-module-label">Module</InputLabel>
              <Select
                labelId="tests-by-module-label"
                id="tests-by-module"
                value={selectedModuleForGrid}
                label="Module"
                onChange={(e) => setSelectedModuleForGrid(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">
                  <em>{summaryData ? 'Select a module' : 'Load summary first'}</em>
                </MenuItem>
                {(summaryData?.byModule || []).map((row) => (
                  <MenuItem key={row.name || '(empty)'} value={row.name === '' ? '(empty)' : row.name}>
                    {row.name === '' ? '(empty)' : row.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {testsByModuleError && (
              <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setTestsByModuleError(null)}>
                {testsByModuleError}
              </Alert>
            )}
            {testsByModuleLoading && (
              <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Loading tests…
                </Typography>
              </Stack>
            )}
            {testsByModule && !testsByModuleLoading && (
              <>
                {testsByModule.stats && testsByModule.stats.testsWithMoreThan2Runs > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {testsByModule.stats.testsWithMoreThan2Runs} test(s) have more than 2 runs (max {testsByModule.stats.maxRunsPerTest} runs).
                  </Typography>
                )}
                <TableContainer sx={{ overflow: 'auto', maxHeight: 560 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell variant="head" sx={{ minWidth: 120 }} sortDirection={gridOrderBy === 'testid' ? gridOrder : false}>
                        <TableSortLabel active={gridOrderBy === 'testid'} direction={gridOrderBy === 'testid' ? gridOrder : 'asc'} onClick={(e) => handleGridSort(e, 'testid')}>
                          testCaseId
                        </TableSortLabel>
                      </TableCell>
                      <TableCell variant="head" sx={{ minWidth: 100 }} sortDirection={gridOrderBy === 'module' ? gridOrder : false}>
                        <TableSortLabel active={gridOrderBy === 'module'} direction={gridOrderBy === 'module' ? gridOrder : 'asc'} onClick={(e) => handleGridSort(e, 'module')}>
                          Module
                        </TableSortLabel>
                      </TableCell>
                      <TableCell variant="head" sx={{ minWidth: 80 }} sortDirection={gridOrderBy === 'instance' ? gridOrder : false}>
                        <TableSortLabel active={gridOrderBy === 'instance'} direction={gridOrderBy === 'instance' ? gridOrder : 'asc'} onClick={(e) => handleGridSort(e, 'instance')}>
                          Instance
                        </TableSortLabel>
                      </TableCell>
                      <TableCell variant="head" sx={{ minWidth: 80 }} sortDirection={gridOrderBy === 'file' ? gridOrder : false}>
                        <TableSortLabel active={gridOrderBy === 'file'} direction={gridOrderBy === 'file' ? gridOrder : 'asc'} onClick={(e) => handleGridSort(e, 'file')}>
                          File
                        </TableSortLabel>
                      </TableCell>
                      <TableCell variant="head" sx={{ minWidth: 140 }} sortDirection={gridOrderBy === 'name' ? gridOrder : false}>
                        <TableSortLabel active={gridOrderBy === 'name'} direction={gridOrderBy === 'name' ? gridOrder : 'asc'} onClick={(e) => handleGridSort(e, 'name')}>
                          Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell variant="head" align="center" sx={{ minWidth: 88 }} sortDirection={gridOrderBy === 'result' ? gridOrder : false}>
                        <TableSortLabel active={gridOrderBy === 'result'} direction={gridOrderBy === 'result' ? gridOrder : 'asc'} onClick={(e) => handleGridSort(e, 'result')}>
                          Result
                        </TableSortLabel>
                      </TableCell>
                      <TableCell variant="head" align="center" sx={{ minWidth: 72 }} sortDirection={gridOrderBy === 'runs' ? gridOrder : false}>
                        <TableSortLabel active={gridOrderBy === 'runs'} direction={gridOrderBy === 'runs' ? gridOrder : 'asc'} onClick={(e) => handleGridSort(e, 'runs')}>
                          Runs
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedGridTests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No tests in this module.
                        </TableCell>
                      </TableRow>
                    ) : sortedGridTests.map((test, idx) => {
                      const s = (test.result || '').toLowerCase();
                      const isPass = s === 'pass';
                      const isFail = s === 'fail';
                      const resultCell = isPass
                        ? <CheckIcon fontSize="small" color="success" aria-label="Pass" />
                        : isFail
                          ? <CloseIcon fontSize="small" color="error" aria-label="Fail" />
                          : <Typography component="span" variant="body2" color="text.secondary">—</Typography>;
                      const runCount = (test.runs || []).length;
                      return (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{test.testid}</TableCell>
                          <TableCell>{test.module || '—'}</TableCell>
                          <TableCell>{test.instance || '—'}</TableCell>
                          <TableCell>{test.file || test.config || '—'}</TableCell>
                          <TableCell title={test.name}>{test.name ? (test.name.length > 40 ? `${test.name.slice(0, 40)}…` : test.name) : '—'}</TableCell>
                          <TableCell align="center">{resultCell}</TableCell>
                          <TableCell align="center">{runCount}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              </>
            )}
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default ReleaseExecutionTab;
